import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Palette, Plus, X, CreditCard, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import {
  Card,
  PageHeader,
  Btn,
  FieldLabel,
  inputCls,
} from "@/components/admin/ui";

export default function Settings() {
  const [s, set] = useState(null);
  const [cat, setCat] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => set(r.data));
  }, []);
  if (!s) return null;

  const save = async (next = s) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        store_name: next.store_name,
        low_stock_threshold: Number(next.low_stock_threshold),
        categories: next.categories,
      });
      set({ ...next, ...data });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };
  const addCat = () => {
    const c = cat.trim();
    if (!c || s.categories.includes(c)) return;
    set({ ...s, categories: [...s.categories, c] });
    setCat("");
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Settings">
        <Btn
          onClick={() => save()}
          disabled={saving}
          data-testid="settings-save-button"
        >
          {saving && <Loader2 size={15} className="animate-spin" />} Save
        </Btn>
      </PageHeader>
      <div className="space-y-4 max-w-3xl">
        <Card title="Store details">
          <div className="grid sm:grid-cols-2 gap-4">
            <label>
              <FieldLabel>Store name</FieldLabel>
              <input
                className={inputCls}
                value={s.store_name}
                onChange={(e) => set({ ...s, store_name: e.target.value })}
                data-testid="settings-store-name-input"
              />
            </label>
            <label>
              <FieldLabel>Currency</FieldLabel>
              <input
                className={`${inputCls} bg-zinc-50`}
                value="US Dollar (USD)"
                disabled
              />
            </label>
            <label>
              <FieldLabel hint="(colors at or below this show on Home)">
                Low stock alert
              </FieldLabel>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={s.low_stock_threshold}
                onChange={(e) =>
                  set({ ...s, low_stock_threshold: e.target.value })
                }
                data-testid="settings-low-stock-input"
              />
            </label>
          </div>
        </Card>
        <Card
          title="Categories"
          description="Used to organize products in your shop. You can also type a new one in the product editor."
        >
          <div
            className="flex flex-wrap gap-2 mb-4"
            data-testid="settings-categories-list"
          >
            {s.categories.length === 0 && (
              <p className="text-sm text-zinc-500">No categories yet.</p>
            )}
            {s.categories.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 h-8 pl-3 pr-1.5 rounded-full bg-zinc-100 text-sm"
              >
                {c}
                <button
                  onClick={() =>
                    set({
                      ...s,
                      categories: s.categories.filter((x) => x !== c),
                    })
                  }
                  className="w-5 h-5 rounded-full hover:bg-zinc-200 flex items-center justify-center"
                  aria-label={`Remove ${c}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 max-w-sm">
            <input
              className={inputCls}
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCat()}
              placeholder="New category"
              data-testid="settings-category-input"
            />
            <Btn
              variant="secondary"
              onClick={addCat}
              data-testid="settings-category-add"
            >
              <Plus size={15} /> Add
            </Btn>
          </div>
        </Card>
        <Card title="Payments" testid="settings-payments-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
              <CreditCard size={18} />
            </div>
            <div className="text-sm flex-1">
              <p className="font-medium">PayPal and payment settings</p>
              <p className="text-zinc-500 mt-1">
                See the active provider and review tax collection status.
              </p>
            </div>
            <Link to="/admin/settings/payments">
              <Btn variant="secondary" data-testid="settings-open-payments">
                Manage
              </Btn>
            </Link>
          </div>
        </Card>
        <Card title="Shipping" testid="settings-shipping-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
              <Truck size={18} />
            </div>
            <div className="text-sm flex-1">
              <p className="font-medium">Shippo rates, packages and tracking</p>
              <p className="text-zinc-500 mt-1">
                Configure protected Shippo settings and manual label purchasing.
              </p>
            </div>
            <Link to="/admin/settings/shipping">
              <Btn variant="secondary" data-testid="settings-open-shipping">
                Manage
              </Btn>
            </Link>
          </div>
        </Card>
        <Card title="Appearance" testid="settings-appearance-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
              <Palette size={18} />
            </div>
            <div className="text-sm flex-1">
              <p className="font-medium">Admin Theme Studio</p>
              <p className="text-zinc-500 mt-1">
                Personalize your private admin dashboard without changing the
                storefront.
              </p>
            </div>
            <Link to="/admin/settings/appearance">
              <Btn variant="secondary" data-testid="settings-open-appearance">
                Customize
              </Btn>
            </Link>
          </div>
        </Card>
        <Card title="Change password">
          <form onSubmit={changePw} className="grid sm:grid-cols-2 gap-4">
            <label>
              <FieldLabel>Current password</FieldLabel>
              <input
                type="password"
                required
                className={inputCls}
                value={pw.current_password}
                onChange={(e) =>
                  setPw({ ...pw, current_password: e.target.value })
                }
                data-testid="settings-current-password"
              />
            </label>
            <label>
              <FieldLabel hint="(min. 8 characters)">New password</FieldLabel>
              <input
                type="password"
                required
                minLength={8}
                className={inputCls}
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                data-testid="settings-new-password"
              />
            </label>
            <div>
              <Btn
                type="submit"
                variant="secondary"
                data-testid="settings-change-password-button"
              >
                Update password
              </Btn>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
